export class PineEconomicAnalysisToolPopover {
  static INFO_POPOVER_CONTENT: Record<string, string> = {
    areaToBePlanted: `Area is based upon the property or paddock selected on previous screen. The value can be manually overwritten.`,

    potentialThinningAndHarvest: `The potential thinning and harvest yields for a pine plantation on this site, as used later in this tool, are
    predicted from the bioregion and enterprise suitability mapping for this location as described below. The yields
    are general estimates only and may vary for your site depending on local conditions not accounted for in the models.`,

    wellSuitedArea: `These figures are derived from automated analysis undertaken for the ‘Crop/Species Decision
    Support Tool’. You can run the ‘Crop/Species Decision Support Tool’ against this location to gain more details on
    the enterprise suitability mapping for this site.`,

    thinning: `If Thinning is set to ‘NA’ this means that thinning is not recommended for this site quality.`,

    allInclusiveCost: `All inclusive establishment cost including:
      <ul class="list-disc pl-2">
      <li>Site preparation, may include clearing, ripping and mounding, spot cultivation and/or access tracks</li>
      <li>Pre-plant weed control</li>
      <li>Seedlings</li>
      <li>Planting</li>
      <li>Fertilising</li>
      <li>Game control</li>
      </ul>
      The PFT Directory lists contractors and suppliers:<br>
      <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"  target="_blank"
      href="https://www.pft.tas.gov.au/directory">www.pft.tas.gov.au/directory</a>`,

    fppEstablishment: `
      An FPP is required if the area to be planted on existing cleared land is greater than 10ha.
      FPPs must be certified by an accredited Forest Practices Officer.<br><br>
      More information available at:<br>
      <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"  target="_blank"
      href="https://www.fpa.tas.gov.au/landowners/forest_practices_plans">
      www.fpa.tas.gov.au/landowners/forest_practices_plans</a>`,

    feesAndLeviesEstablishment: `
      FPP fee, all FPPs attract a fee payable to the Forest Practices Authority. More info at
      <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"  target="_blank"
      href="https://www.fpa.tas.gov.au/landowners/forest_practices_plans">www.fpa.tas.gov.au/landowners/forest_practices_plans</a>`,

    ptr: `Private Timber Reserve (PTR) application fee, applying for a PTR streamlines future planning activities.
    More info at <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer" target="_blank"
      href="https://www.pft.tas.gov.au/private-timber-reserves-ptr">www.pft.tas.gov.au/private-timber-reserves-ptr</a>`,

    useDefaultEstablishment: `
      Default values assumptions:
      <ul class="list-disc pl-2">
      <li>Site preparation ripping and mounding $750 per ha</li>
      <li>Pre-plant weed control one broadacre application by tractor ($100 per ha)</li>
      <li>Seedlings planted open rooted seedlings at 1000 stems per ha ($100 per ha)</li>
      <li>Planting by spade ($100 per ha)</li>
      <li>Fertilising multicoat at time of planting ($100 per ha)</li>
      <li>Game control ($100 per ha)</li>
      <li>FPP preparation and compliance ($2000 per site)</li>
      <li>Fees and Levies ($150 FPA levy, $670 PFT levy)</li>
      </ul>`,

    postEstablishmentTotal: `
    All inclusive post-establishment cost assumed to incur at age 1 including:
    <ul class="list-disc pl-2">
    <li>Post-plant weed control</li>
    <li>Refill planting to increase stocking if significant browsing or tree death has occurred</li>
    <li>Game control is generally required until trees are at least 1-2m tall</li>
    <li>Compliance reporting includes a survival assessment and reporting requirements to meet FPP conditions</li>`,

    AnnualMaintenanceCost: `
      Annual costs may include:
      <ul class="list-disc pl-2">
      <li>Monitoring</li>
      <li>Insurance</li>
      <li>Access maintenance e.g. firebreaks</li>
      </ul>`,

    useDefaultPostEstablishment: `
      Default values assumptions:
      <ul class="list-disc pl-2">
      <li>Post-plant weed control one weed-a-metre application ($250 per ha)</li>
      <li>Refill planting by spade 50 seedlings per ha ($250 per ha)</li>
      <li>Game control ($150 per ha)</li>
      <li>Compliance Reporting ($20 per ha)</li>
      <li>Monitoring one site visit per year ($20 per ha)</li>
      <li>Insurance not included ($0 per ha)</li>
      <li>Access maintenance ($10 per ha)</li>
      </ul>`,

    modelCarbon: `Carbon credits can be earned using the Australian Carbon Credit Unit Scheme’s Plantation Forestry Method. Schedule 1 of this scheme applies to trees planted on agricultural land or land that has been fallow since a previous plantation of 7 years or greater.`,

    establishmentCostCarbon: `
      Carbon forestry projects must be registered with the Clean Energy Regulator before ACCUs can be earned.
      While there is no direct government registration fee, most landowners engage a carbon project manager or
      consultant to assist with project design, registration and ongoing compliance requirements. Costs entered here
      should reflect those services and any associated project establishment expenses’`,

    annualOffsetReportCarbon: `ACCUs are delivered following submission of an offset report.  An offset report is required each time you apply for ACCU’s and you can nominated to do this periodically, the period you set can range from 6 months to 5 years. `,

    auditCarbon: `An audit is required with the first offset report in the first year, and then two more times five years apart if the plantation is greater than 200ha`,

    accuSalesCarbon: `
      Australian Carbon Credit Units (ACCUs) can be sold to purchasers looking to offset their own emissions.`,

    useDefaultCarbon: `Default values assumptions:
      <ul class="list-disc pl-2">
      <li>Carbon Establishment Cost ($2000 per site)</li>
      <li>Offset Report ($2000 per site)</li>
      <li>Compliance Cost ($15,000 at age 5 if less than 20ha, or $15k at age 5, $15k at age 10, $15k at age 15)</li>
      <li>ACCU Sale Price ($35 per ACCU) based on spot price July 2025</li>
      </ul>`,

    thinningHeaderInfo: `<ul class="list-disc pl-2">
<li>Small sawlogs, minimum 30-40cm small end diameter, 5.5m length</li>
<li>Posts, minimum 20cm small end diameter, 5.5m length, limited sweep</li>
<li>Pulplogs, minimum 8cm small end diameter, 5.5m length</li>
</ul>`,

    thinningHarvestCost: `Harvest cost varies per product due to processing and log requirements.`,

    thinningDistanceToMill: `Different products may go to different mills. The unit rate ($/km) for transport is relatively known constant.`,

    thinningLogPrice: `Log price will vary per product`,

    thinningFpp: `An FPP is required if greater than 100t of product is to be harvested.
    FPPs must be certified by an accredited Forest Practices Officer.
    More information available at
    <a target="_blank" class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
    href="https://www.fpa.tas.gov.au/landowners/forest_practices_plans">www.fpa.tas.gov.au/landowners/forest_practices_plans</a>`,

    thinningFeesLevies: `<ul class="list-disc pl-2">
<li>FPP fee, all FPPs attract a fee payable to the Forest Practices Authority. More info at
<a href="https://www.fpa.tas.gov.au/landowners/forest_practices_plans"
target="_blank" class="text-blue-600 underline hover:text-blue-800 cursor-pointer">
www.fpa.tas.gov.au/landowners/forest_practices_plans</a></li>
<li>Private Forest Tasmania levy is waived on thinning operations that occur on private land.</li>
</ul>`,

    thinningRoadMaintenance: `Include costs associated with maintaining on-location roads such as pot holing, grading or
    culvert replacements. Increase this cost if construction of roading is required.`,

    useDefaultThinning: `Default values assumptions:
<ul class="list-disc pl-2">
<li>Product 1 = pulpwood</li>
<li>Product 2 = small unpruned sawlogs</li>
<li>Product 3 = posts</li>
<li>FPP $2000 per site</li>
<li>Fees and levies ($150 FPA levy, $170 PFT levy)</li>
<li>Road maintenance ($ 1 /t) with 1km road length</li>
</ul>`,

    harvestHeaderInfo: `<ul class="list-disc pl-2">
<li>Large sawlogs, minimum 40cm plus small end diameter, 5.5m length</li>
<li>Small sawlogs, minimum 30-40cm small end diameter, 5.5m length</li>
<li>Pulplogs, minimum 8cm small end diameter, 5.5m length</li>
</ul>`,

    harvestCost: `Harvest cost varies per product due to processing and log requirements. `,

    harvestDistanceToMill: `Different products may go to different mills.
    The unit rate ($/km) for transport is relatively known constant.`,

    harvestLogPrice: `Log price will vary per product`,

    harvestFpp: `An FPP is required if greater than 100t of product is to be harvested. FPPs must be certified by an accredited Forest Practices Officer.
    More information available at
    <a href="https://www.fpa.tas.gov.au/landowners/forest_practices_plans"
    target="_blank" class="text-blue-600 underline hover:text-blue-800 cursor-pointer">
    www.fpa.tas.gov.au/landowners/forest_practices_plans</a>`,

    harvestFeesLevies: `<ul class="list-disc pl-2">
<li>FPP fee, all FPPs attract a fee payable to the Forest Practices Authority. More info at
<a href="https://www.fpa.tas.gov.au/landowners/forest_practices_plans"
target="_blank" class="text-blue-600 underline hover:text-blue-800 cursor-pointer">www.fpa.tas.gov.au/landowners/forest_practices_plans</a></li>
<li>Private Forest Tasmania levy is payable on all harvesting operations that occur on private land.</li>
</ul>`,

    roadMaintenance: `Include costs associated with maintaining on-location roads such as pot holing, grading or culvert replacements.
    Increase this cost if construction of roading is required.`,

    harvestUseDefaultValues: `Default values assumptions:
<ul class="list-disc pl-2">
<li>Product 1 = pulpwood</li>
<li>Product 2 = small unpruned sawlogs</li>
<li>Product 3 = large unpruned sawlogs</li>
<li>FPP $2000 per site</li>
<li>Fees and levies ($150 FPA levy, $170 PFT levy)</li>
</ul>`,

    discountRate: `Rate use to calculate the present value of future cash flows.
    The higher the % the more future cash flows are discounted to present value.`
  };

  static DOLLAR_POPOVER_CONTENT: Record<string, string> = {
    sitePrep: `Site preparation and Cultivation ($500 - 3000 per ha) dependent upon existing fuel loads (slash from previous harvest),
the level of weed control, soil type and machinery selection. Spot cultivation tends to be more expensive than line rip and mound.
Line rip and mound may not be cost effective on steep, rocky or second rotation sites.`,

    estWeedCtrl: `Pre-plant weed control ($ 0 to 400 per ha) dependent upon application method and herbicide products.`,

    seedlings: `Seedlings ($0.3 to 1 per seedling) dependent upon type (open rooted vs potted) and the level of genetics. This tool assumes planting at 1000 stems per ha.`,

    planting: `Planting ($250 to 600 per ha) dependent upon the terrain, cultivation, and seedling type.`,

    fertilising: `Fertiliser ($150 to 300 per ha) lower application costs are achieved if combined with planting, timing of application will determine product selection.`,

    estGameCtrl: `Game control ($0 to $1000 per ha) can be highly variable and needs to be responsive to browsing pressure.
    Although expensive, wildlife-proof fencing to exclude native animals is recommended where browsing pressure is expected to be high.
    The Farm Woodlot Tool can be used to estimate fencing requirements and costs.`,

    estFpp: `Generally relatively fixed price, may increase for very large or complicated FPPs`,

    estFeesLevies: `FPP fee ({0} per ha) - dependent upon the type of FPP and ha. Minimum fees apply for areas less than 5ha`,

    ptr: `PTR application fee ({0} per application)`,

    postEstWeedCtrl: `Post-plant weed control ($ 0 to 400 per ha) dependent upon application method and herbicide products.`,

    refillPlanting: `Refill planting ($0 to 500 per ha) dependent upon survival rate, seedling cost, planting cost and browsing pressure.`,

    postEstGameCtrl: `Game control ($0 to 1000 per ha) can be highly variable and needs to be responsive to browsing pressure.`,

    complianceReporting: `Compliance reporting ($20 to 50 per ha) dependent upon scale and access conditions`,

    annualMaintenanceCost: `<ul class="list-disc pl-2"><li>Monitoring ($10 to 40 per ha) to check growth, health and any emerging issues</li>
<li>Insurance ($0 to 300 per ha) dependent upon exclusions and excess selected</li>
<li>Access maintenance ($0 to 10 per ha) dependent upon site conditions and seasonal weather</li></ul>`,

    establishmentCost: `($1000 to 4000 per site) dependent upon service provider as it may be charged on an hourly rate or included as component of an inclusive management arrangement.`,

    annualOffset: `Offset Report ($500 to 1000 per site) cost to compile and submit report may be charged on a hourly rate or may be included as component of an inclusive management arrangement with service provider `,

    carbonAudit: `Audit ($15,000 per audit per site) relatively fixed price independent of scale. `,

    carbonACCU: `ACCU spot price is a variable commodity`,

    thinningHarvestCost: `($40 to 80 per tonne) pulpwood at the lower end of range, sawlogs at high end of range.`,

    thinningLogPrice: `($50 to 200 per tonne) lower prices are achieved for pulpwood and higher prices for sawlogs`,

    thinningFPP: `Generally relatively fixed price, may increase for very large or complicated FPPs`,

    thinningFeesLevies: `FPP fee ({0} per ha) - dependent upon the type of FPP and ha. Minimum fees apply for areas less than 5ha`,

    thinningRoadMaintenance: `Road maintenance ($0 to 2 per tonne) is dependent upon road length, condition, and if any construction is required.`,

    harvestCost: `($40 to 80 per tonne) pulpwood at the lower end of range, sawlogs at high end of range.`,

    harvestLogPrice: `($50 to 200 per tonne) lower prices are achieved for pulpwood and higher prices for sawlogs`,

    harvestFPP: `Generally relatively fixed price, may increase for very large or complicated FPPs`,

    harvestFeesLevies: `<ul class="list-disc pl-2"><li>FPP fee ({0} per ha) - dependent upon the type of FPP and ha. Minimum fees apply for areas less than 5ha</li>
<li>PFT levy ($17.85 per ha as at Jul 2026)</li></ul>`,

    harvestRoadMaintenance: `Road maintenance ($0 to 2 per tonne) is dependent upon road length, condition, and if any construction is required.`
  };
}
